class BrandUrlValidator {
    static BRAND_TYPES = Object.freeze({
        a: { name: 'agent', label: 'Agent', description: 'Individual real estate agent' },
        l: { name: 'lender', label: 'Lender', description: 'Mortgage lender' },
        b: { name: 'broker', label: 'Broker', description: 'Real estate broker' },
        t: { name: 'team', label: 'Team', description: 'Real estate team' },
        o: { name: 'office', label: 'Office', description: 'Real estate office' }
    });

    static SLUG_PATTERN = /^[a-zA-Z0-9_-]+$/;
    static MIN_SLUG_LENGTH = 2;
    static MAX_SLUG_LENGTH = 100;

    static getBrandType(identifier, byName = false) {
        if (!identifier || typeof identifier !== 'string') return null;
        const id = identifier.toLowerCase();

        if (byName) {
            const entry = Object.entries(this.BRAND_TYPES).find(([, info]) => info.name === id);
            return entry ? { code: entry[0], ...entry[1] } : null;
        } else {
            const info = this.BRAND_TYPES[id];
            return info ? { code: id, ...info } : null;
        }
    }

    static validateSlug(slug) {
        const errors = [];

        if (!slug) {
            errors.push('Slug is required');
            return { isValid: false, errors };
        }

        if (typeof slug !== 'string') {
            errors.push('Slug must be a string');
            return { isValid: false, errors };
        }

        if (slug.length < this.MIN_SLUG_LENGTH) {
            errors.push(`Slug must be at least ${this.MIN_SLUG_LENGTH} characters`);
        }

        if (slug.length > this.MAX_SLUG_LENGTH) {
            errors.push(`Slug must be no more than ${this.MAX_SLUG_LENGTH} characters`);
        }

        if (!this.SLUG_PATTERN.test(slug)) {
            errors.push('Slug can only contain letters, numbers, hyphens, and underscores');
        }

        return { isValid: errors.length === 0, errors };
    }

    static parseBrandPath(path) {
        if (!path || typeof path !== 'string') {
            return { isValid: false, error: 'Invalid path provided' };
        }

        const cleanPath = path.trim().replace(/\/+/g, '/');
        const segments = cleanPath.split('/').filter((segment) => segment.length > 0);

        if (segments.length < 2) {
            return { isValid: false, error: 'Path must contain at least brand type and slug' };
        }

        const [typeCode, slug, ...additionalSegments] = segments;

        const brandType = this.getBrandType(typeCode);
        if (!brandType) {
            return {
                isValid: false,
                error: `Invalid brand type '${typeCode}'. Valid types: ${Object.keys(this.BRAND_TYPES).join(', ')}`
            };
        }

        const slugValidation = this.validateSlug(slug);
        if (!slugValidation.isValid) {
            return {
                isValid: false,
                error: `Invalid slug: ${slugValidation.errors.join(', ')}`
            };
        }

        return {
            isValid: true,
            type: brandType,
            slug,
            additionalSegments,
            fullPath: cleanPath
        };
    }

    static validateBrandUrl(url) {
        if (!url || typeof url !== 'string') {
            return { isValid: false, error: 'Invalid URL provided' };
        }

        let urlObj;
        try {
            urlObj = new URL(url);
        } catch (error) {
            return { isValid: false, error: 'Invalid URL format' };
        }

        const pathResult = this.parseBrandPath(urlObj.pathname);

        return {
            ...pathResult,
            url: url,
            domain: urlObj.hostname,
            protocol: urlObj.protocol
        };
    }

    static isValidBrand(urlOrPath) {
        try {
            new URL(urlOrPath);
            return this.validateBrandUrl(urlOrPath).isValid;
        } catch {
            return this.parseBrandPath(urlOrPath).isValid;
        }
    }
}

// Export convenience object
export const BrandValidator = {
    isValid: (urlOrPath) => BrandUrlValidator.isValidBrand(urlOrPath),
    parse: (urlOrPath) => {
        try {
            new URL(urlOrPath);
            return BrandUrlValidator.validateBrandUrl(urlOrPath);
        } catch {
            return BrandUrlValidator.parseBrandPath(urlOrPath);
        }
    },
    getTypes: () => BrandUrlValidator.BRAND_TYPES,
    getValidCodes: () => Object.keys(BrandUrlValidator.BRAND_TYPES),
    getBrandType: (id, byName) => BrandUrlValidator.getBrandType(id, byName)
};

export default BrandValidator;
